import React, { useState } from 'react';
import Zirodelta from '@/assets/dummies/zirodelta.svg';
import IconVerified from '@/assets/icons/verified.svg';
import IconShield from '@/assets/icons/shield.svg';
import IconGlobe from '@/assets/icons/globe.svg';
import IconX from '@/assets/icons/x-plain.svg';
import IconDiscord from '@/assets/icons/discord.svg';
import IconTelegram from '@/assets/icons/telegram-plain.svg';
import IconInstagram from '@/assets/icons/instagram.svg';
import IconDoubleChevronsRight from '@/assets/icons/double_chevrons_right.svg';
import IconDoubleChevronsLeft from '@/assets/icons/double_chevrons_left.svg';
import IconAlertTriangle from '@/assets/icons/alert_triangle.svg';
import { Dialog } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import SocialBadgeModal from '@/components/SocialBadgeModal';
import useSidebarStore from '@/stores/useSidebarStore';
import useModalStore from '@/stores/useModalStore';
import { PartnerSidebarSkeleton } from '@/components/skeletons/PartnerSidebarSkeleton';
import { TimelineSkeleton } from '@/components/skeletons/TimelineSkeleton';
import { twitterApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { cnftApi } from '@/services/api/cnftApi';
import { isValidBetweenDates } from '@/utils/TimeUtils';
import { trackEvent } from '@/utils/analytics';
import { formatTimelineDateRange } from '@/lib/utils';
import { EventAction } from '@/types/analytics';
import { useParams } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getPartnerProjectsByIdOptions, postJoinPartnerOptions } from '@/services/queryOptions/partnerQueryOptions';
import { queryClient } from '@/lib/queryClient';
import { partnerKeys, questKeys } from '@/constant/queryKeys';
import usePartner from '@/hooks/usePartner';
import usePartnerLastActiveProject from '@/hooks/usePartnerLastActiveProject';
import usePartnerJoinStatus from '@/hooks/usePartnerJoinStatus';

function Sidebar() {
    const { toast } = useToast();
    const showBadgeModal = useModalStore((state) => state.showBadgeModal);
    const setShowBadgeModal = useModalStore((state) => state.setShowBadgeModal);
    const isOpenDaoSidebar = useSidebarStore((state) => state.isOpenDaoSidebar);
    const setIsOpenDaoSidebar = useSidebarStore((state) => state.setIsOpenDaoSidebar);

    const params = useParams<{ slug: string }>();
    const {data: partner, isLoading: isLoadingPartner} = usePartner();
    const {data: projects, isLoading: isLoadingProjects} = useQuery(getPartnerProjectsByIdOptions(partner?.id));
    const {data: project} = usePartnerLastActiveProject();
    const {data: status} = usePartnerJoinStatus();
    const {mutateAsync: joinPartner} = useMutation(postJoinPartnerOptions());

    // Tweet verification state
    const [tweetUrl, setTweetUrl] = useState("");
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validationReasons, setValidationReasons] = useState<string[]>([]);

    // Social badge state
    const [socialBadge, setSocialBadge] = useState<{ url: string; isPreparing: boolean }>({ url: "", isPreparing: false });

    const handleJoinDAO = async () => {
        // Track Join DAO button click
        trackEvent('dao', EventAction.BUTTON_CLICK, {
            label: 'join_dao_modal',
            component: 'xdao_sidebar',
            partner_name: partner?.name,
        });

        if (!partner?.id) {
            toast({
                title: "Error",
                description: "Partner not found",
                variant: "destructive",
            });
            return;
        }

        try {
            if (socialBadge.url.length === 0) {
                setSocialBadge({ ...socialBadge, isPreparing: true });
            }

            // Call new check-or-generate endpoint
            const response = await joinPartner({ partnerId: partner?.id });

            // Store the badge URL in state
            setSocialBadge({ url: response.socialBadgeAddress, isPreparing: false });
            // Open modal with the generated/existing badge
            setShowBadgeModal(true);
        } catch (error) {
            console.error('Failed to generate/fetch badge:', error);
            toast({
                title: "Error",
                description: "Failed to generate badge. Please try again.",
                variant: "destructive",
            });
            setSocialBadge({ ...socialBadge, isPreparing: false });
        }
    };

    const handleViewSocialBadge = async () => {
        // Track My Social Badge button click (for users who already joined)
        trackEvent('dao', EventAction.BUTTON_CLICK, {
            label: 'view_social_badge',
            component: 'xdao_sidebar',
            partner_name: partner?.name,
        });

        if (!partner?.id) {
            toast({
                title: "Error",
                description: "Partner not found",
                variant: "destructive",
            });
            return;
        }

        try {
            if (socialBadge.url.length === 0) {
                setSocialBadge({ ...socialBadge, isPreparing: true });
            }

            // Call endpoint to get existing badge
            const response = await joinPartner({ partnerId: partner?.id });

            // Store the badge URL in state
            setSocialBadge({ url: response.socialBadgeAddress, isPreparing: false });
            // Open modal with the existing badge
            setShowBadgeModal(true);
        } catch (error) {
            console.error('Failed to fetch badge:', error);
            toast({
                title: "Error",
                description: "Failed to load badge. Please try again.",
                variant: "destructive",
            });
            setSocialBadge({ ...socialBadge, isPreparing: false });
        }
    };

    const handleValidateAndJoin = async () => {
        // Track Verify & Join DAO button click attempt
        trackEvent('dao', EventAction.BUTTON_CLICK, {
            label: 'verify_join_dao_attempt',
            component: 'xdao_sidebar',
            partner_name: partner?.name,
        });

        if (!partner?.id) {
            toast({
                title: "Error",
                description: "Partner not found",
                variant: "destructive",
            });
            return;
        }

        if (!project?.id) {
            setValidationError("There's no active project");
            return;
        }

        // Reset errors
        setValidationError(null);
        setValidationReasons([]);

        // Validate URL format
        if (!tweetUrl.trim()) {
            setValidationError("Please enter a tweet URL");
            return;
        }

        // Basic URL validation
        const tweetUrlPattern = /(?:twitter\.com|x\.com)\/.*\/status\/\d+/;
        if (!tweetUrlPattern.test(tweetUrl)) {
            setValidationError("Please enter a valid tweet URL");
            return;
        }

        setIsValidating(true);

        try {
            const response = await twitterApi.validateAndJoin(partner?.id, tweetUrl, project.id);

            if (response.valid && response.joined) {
                // Track successful DAO join
                trackEvent('dao', EventAction.DAO_JOIN, {
                    label: 'dao_join_success',
                    component: 'xdao_sidebar',
                    partner_name: partner?.name,
                });

                // Success - update store and show success message
                queryClient.setQueryData(partnerKeys.getJoinStatusById(partner?.id), () => ({
                    hasJoined: true,
                }));

                // Refetch partner data to get updated totalMember count
                queryClient.invalidateQueries({ queryKey: partnerKeys.getBySlug(params.slug) });

                // Refetch quests to update completion status (so CardQuest button shows "COMPLETED")
                queryClient.invalidateQueries({ queryKey: questKeys.getWithUserStatusById(partner?.id) });

                // Mint CNFT with the badge URL
                await cnftApi.mintCNFT({ partnerId: partner?.id });

                toast({
                    title: "Success!",
                    description: response.message || "You have successfully joined the DAO!",
                });

                // Reset form
                setTweetUrl("");
                setValidationError(null);
                setValidationReasons([]);
            } else {
                // Track failed validation
                trackEvent('dao', 'dao_join_failed', {
                    label: 'dao_join_failed',
                    component: 'xdao_sidebar',
                    partner_name: partner?.name,
                    error_message: response.message || "Tweet validation failed",
                    validation_reasons: response.reasons?.join(', ')
                });

                // Validation failed
                setValidationError(response.message || "Tweet validation failed");
                setValidationReasons(response.reasons || []);
            }
        } catch (error: any) {
            console.error("Validation error:", error);

            const errorMessage = error.response?.data?.error || error.message || "Failed to validate tweet. Please try again.";

            // Track error
            trackEvent('dao', 'dao_join_failed', {
                label: 'dao_join_failed',
                component: 'xdao_sidebar',
                partner_name: partner?.name,
                error_message: errorMessage
            });

            // Handle different error types
            if (error.response?.data?.error) {
                setValidationError(error.response.data.error);
            } else if (error.message) {
                setValidationError(error.message);
            } else {
                setValidationError("Failed to validate tweet. Please try again.");
            }
        } finally {
            setIsValidating(false);
        }
    };

    if (isLoadingPartner) {
        return <PartnerSidebarSkeleton />;
    }

    if (!partner) {
        return (
            <div className="w-full mt-[2.88rem]">
                <p className="text-white font-inter">Partner not found</p>
            </div>
        );
    }

    const sidebarContent = (
        <div className="w-full mt-[2.88rem]">
        <div className="mb-[1.88rem] h-[6.88rem] w-[6.88rem] rounded-full flex flex-row items-center">
            <img
                src={partner.logo || Zirodelta}
                alt={partner.name}
                className='w-full h-full object-contain'
            />
            {partner.featuringPartner?.logoUrl && (
                <img
                    src={partner.featuringPartner.logoUrl}
                    alt={partner.name}
                    className='w-full h-full object-contain -ml-10'
                />
            )}
        </div>
        <div className="mb-[1.88rem]">
            {partner.isVerified && (
                <div className="flex flex-row items-center mb-2">
                <div className="w-[1.13rem] h-[1.13rem] rounded-full mr-2">
                    <img
                        src={IconVerified}
                        alt="verified"
                        className='w-full h-full object-contain'
                    />
                </div>
                <span className="text-tertiary text-xs font-inter">Verified</span>
                </div>
            )}
            <span className="text-white font-tiny5 text-5xl">
                {partner.name}
            </span>
        </div>

        {project?.isNeedJoinDao && (
            <div className="grid grid-cols-3 mb-[1.88rem]">
                <div className="flex flex-col">
                <span className="font-tiny5 text-2xl text-white">{partner.totalMember?.toLocaleString() || 0}</span>
                <span className="font-inter text-tertiary text-sm">DAOs Member</span>
                </div>
                <div className="flex flex-col">
                <span className="font-tiny5 text-2xl text-white">{partner.totalNftsMinted?.toLocaleString() || 0}</span>
                <span className="font-inter text-tertiary text-sm">NFTs Minted</span>
                </div>
                <div className="flex flex-col">
                <span className="font-tiny5 text-2xl text-white">{partner.totalDistributedPoints ? (partner.totalDistributedPoints >= 1000000 ? `${(partner.totalDistributedPoints / 1000000).toFixed(1)}M+` : partner.totalDistributedPoints.toLocaleString()) : 0}</span>
                <span className="font-inter text-tertiary text-sm">Points Distributed</span>
                </div>
            </div>
        )}

        {(project?.isNeedJoinDao && status?.hasJoined) ? (
            // Need to hide it for now, as it's not ready yet
            <div className="grid grid-cols-[1fr_22px]">
                <div className="flex flex-row flex-wrap">
                {/* <div className="flex flex-row items-center py-2 px-4 border-purple-600 border-2 rounded-xl mr-5 mb-5">
                    <div className="w-[16px] h-[16px] mr-1">
                        <img
                            src={IconDollar}
                            alt="dollar"
                            className='w-full h-full object-contain'
                        />
                    </div>
                    <span className="font-inter text-md font-semibold text-white">Buy $ZDLT</span>
                </div>
                <div className="flex flex-row items-center py-2 px-4 border-purple-600 border-2 rounded-xl mr-5 mb-5">
                    <div className="w-[16px] h-[16px] mr-1">
                        <img
                            src={IconGift}
                            alt="gift"
                            className='w-full h-full object-contain'
                        />
                    </div>
                    <span className="font-tiny5 text-md text-white">Goodtonium</span>
                </div> */}
                <Dialog open={showBadgeModal} onOpenChange={setShowBadgeModal}>
                    <button
                        className="flex flex-row items-center py-2 px-4 border-purple-600 border-2 rounded-xl mr-5 mb-5"
                        onClick={handleViewSocialBadge}
                    >
                        <div className="w-[22px] h-[22px] mr-1">
                            <img
                                src={IconShield}
                                alt="shield"
                                className='w-full h-full object-contain'
                            />
                        </div>
                        <span className="font-inter text-md font-semibold text-white">My Social Badge</span>
                    </button>
                    <SocialBadgeModal socialBadgeUrl={socialBadge.url} onlyShowDownload partner={partner} />
                </Dialog>
                </div>

                {/* <button className="h-[22px] w-[22px]">
                    <img
                        src={IconMore}
                        alt="more"
                        className='w-full h-full object-contain'
                    />
                </button> */}
            </div>
        ) : (project?.isNeedJoinDao && !status?.hasJoined) ? (
            <>
                <Dialog open={showBadgeModal} onOpenChange={setShowBadgeModal}>
                    <button
                        // JOIN DAO Disable for preview purpose
                        disabled
                        className='flex flex-row items-center py-2 bg-[#873ED0] w-full justify-center rounded-xl mb-[1.88rem] disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={handleJoinDAO}
                    >
                        <img
                            src={IconDoubleChevronsRight}
                            alt="double-chevron-right"
                            className='w-[20px] h-[20px] object-contain'
                        />
                        <span className='font-inter font-semibold text-semibold text-white mx-[0.63rem]'>
                            {socialBadge.isPreparing ? 'Preparing...' : 'Join DAO'}
                        </span>
                        <img
                            src={IconDoubleChevronsLeft}
                            alt="double-chevron-left"
                            className='w-[20px] h-[20px] object-contain'
                        />
                    </button>
                    <SocialBadgeModal socialBadgeUrl={socialBadge.url} partner={partner} />
                </Dialog>

                {/* Tweet Verification Section */}
                <div className="mb-[1.88rem]">
                    {/* Reminder Text */}
                    <div className="flex items-start bg-[#1F2937]/30 border border-[#873ED0]/30 rounded-lg p-3 mb-3">
                        <img
                            src={IconAlertTriangle}
                            alt="info"
                            className="w-5 h-5 mr-3 opacity-80"
                        />
                        <p className="font-inter text-white/80 text-sm">
                            After sharing your badge on X, paste your tweet link below to verify and join!
                        </p>
                    </div>

                    {/* Tweet URL Input */}
                    <input
                        type="text"
                        value={tweetUrl}
                        onChange={(e) => setTweetUrl(e.target.value)}
                        placeholder="Paste your tweet link here..."
                        className="w-full px-4 py-3 bg-[#0E1016] border border-[#1F2937] rounded-lg text-white font-inter text-sm focus:outline-none focus:border-[#873ED0] transition-colors mb-3"
                        disabled={isValidating}
                    />

                    {/* Validation Error Display */}
                    {validationError && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
                            <p className="font-inter text-red-400 text-sm font-semibold mb-1">
                                {validationError}
                            </p>
                            {validationReasons.length > 0 && (
                                <ul className="list-disc list-inside space-y-1">
                                    {validationReasons.map((reason, index) => (
                                        <li key={index} className="font-inter text-red-400/80 text-xs">
                                            {reason}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Verify & Join Button */}
                    <button
                        onClick={handleValidateAndJoin}
                        disabled={isValidating || !tweetUrl.trim()}
                        className="w-full flex items-center justify-center py-3 px-4 bg-[#873ED0] rounded-xl text-white font-inter font-semibold text-base hover:bg-[#9B4FE8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isValidating ? (
                            <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Verifying...
                            </>
                        ) : (
                            'Verify & Join DAO'
                        )}
                    </button>
                </div>
            </>
        ) : null}

        <div className="mb-[1.88rem]">
            <p className="font-inter text-tertiary">
                {partner.titleDescription}
            </p>
            <p className="font-inter text-tertiary">
            {partner.description || "No description available"}
            </p>
        </div>

        {/* Social Links */}
        <div className="flex flex-row mb-[1.88rem] items-center">
            {partner.webUrl && (
                <a
                    href={partner.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_website',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.name,
                            destination: partner.webUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconGlobe}
                        alt="globe"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.xUrl && (
                <a
                    href={partner.xUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_x',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.name,
                            destination: partner.xUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconX}
                        alt="x"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.discordUrl && (
                <a
                    href={partner.discordUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_discord',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.name,
                            destination: partner.discordUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconDiscord}
                        alt="discord"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.telegramUrl && (
                <a
                    href={partner.telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_telegram',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.name,
                            destination: partner.telegramUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconTelegram}
                        alt="telegram"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.instagramUrl && (
                <a
                    href={partner.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_telegram',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.name,
                            destination: partner.instagramUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconInstagram}
                        alt="instagram"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}

            {partner.featuringPartner && (
                <div className='flex flex-row items-center mr-5 gap-5'>
                    <span className='text-tertiary font-inter text-sm'>{partner.socialMediaName}</span>
                    <div className='w-0.5 h-7 bg-borderTertiary'/>
                </div>
            )}

            {partner.featuringPartner?.webUrl && (
                <a
                    href={partner.featuringPartner?.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_website',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.featuringPartner?.name,
                            destination: partner.featuringPartner?.webUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconGlobe}
                        alt="globe"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.featuringPartner?.xUrl && (
                <a
                    href={partner.featuringPartner?.xUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_x',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.featuringPartner?.name,
                            destination: partner.featuringPartner?.xUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconX}
                        alt="x"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.featuringPartner?.discordUrl && (
                <a
                    href={partner.featuringPartner?.discordUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_discord',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.featuringPartner?.name,
                            destination: partner.featuringPartner?.discordUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconDiscord}
                        alt="discord"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.featuringPartner?.telegramUrl && (
                <a
                    href={partner.featuringPartner?.telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_telegram',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.featuringPartner?.name,
                            destination: partner.featuringPartner?.telegramUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconTelegram}
                        alt="telegram"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}
            {partner.featuringPartner?.instagramUrl && (
                <a
                    href={partner.featuringPartner?.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border-2 border-borderTertiary rounded-xl mr-5"
                    onClick={() => {
                        trackEvent('ui_interaction', EventAction.LINK_CLICK, {
                            label: 'partner_telegram',
                            component: 'xdao_sidebar',
                            location: 'social_links',
                            link_type: 'external',
                            partner_name: partner.featuringPartner?.name,
                            destination: partner.featuringPartner?.instagramUrl
                        });
                    }}
                >
                <div className="w-[1.38rem] h-[1.38rem]">
                    <img
                        src={IconInstagram}
                        alt="instagram"
                        className='w-full h-full object-contain'
                    />
                </div>
                </a>
            )}

            {partner.featuringPartner?.name && (
                <span className='text-tertiary font-inter text-sm'>{partner.featuringPartner.name}</span>
            )}
        </div>

        {/* Timeline */}
        {!partner.isMarketplaceProject && (
            <div>
                <p className="text-xl text-white font-inter font-semibold mb-4">
                Timeline
                </p>

                {isLoadingProjects ? (
                    <TimelineSkeleton count={2} />
                ) : projects ? (
                    projects.map((item, index) => {
                        // Compute status based on dates and isActive flag
                        const now = new Date();
                        const startDate = new Date(item.startDate);
                        const endDate = new Date(item.endDate);
                        let computedStatus: 'upcoming' | 'active' | 'completed' = 'upcoming';
                    
                        if (isValidBetweenDates(startDate, endDate, now)) {
                            computedStatus = 'active';
                        } else if (now > endDate) {
                            computedStatus = 'completed';
                        }

                        // Calculate countdown timer
                        const targetDate = computedStatus === 'active' ? endDate : startDate;
                        const timeDiff = targetDate.getTime() - now.getTime();
                        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

                        const isLast = index === projects.length - 1;
                        const isActive = computedStatus === 'active';
                        const isCompleted = computedStatus === 'completed';

                        return (
                            <div key={item.id} className="mb-3">
                                <div className="grid grid-cols-[24px_1fr] gap-4 mb-2">
                                    <div className="flex items-center w-6 h-6 justify-center">
                                    <div className={`w-4 h-4 rounded-full border-1 flex items-center justify-center ${isActive ? 'bg-[rgba(0,153,81,0.5)] border-[#14AE5C]' : ''}`}>
                                        <div className={`w-[0.63rem] h-[0.63rem] rounded-full ${isActive ? 'bg-[#14AE5C] animate-pulse' : 'bg-white'}`}></div>
                                    </div>
                                    </div>
                                    <div className="flex flex-row justify-between">
                                    <span className="font-inter text-base font-semibold text-white">{item.title}</span>
                                    <span className="font-inter text-sm text-tertiary">
                                        {formatTimelineDateRange(item.startDate, item.endDate)}
                                    </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-[24px_1fr] gap-4">
                                    {/* Dashed line - only show if not last */}
                                    <div className="flex flex-row w-6 self-stretch justify-center pt-1">
                                    {!isLast && (
                                        <div className="w-0 h-full border-l-2 border-[#D9D9D9] border-dotted">
                                        </div>
                                    )}
                                    </div>

                                    {/* Status container - show for all items */}
                                    <div className="flex flex-row items-center justify-between px-3 py-2 bg-[#171921] border border-borderGray rounded-lg mb-6">
                                    <span className={`font-inter text-sm font-medium ${isActive ? 'text-[#14AE5C]' : 'text-tertiary'}`}>
                                        {isCompleted ? 'ENDED' : isActive ? 'LIVE' : 'Upcoming'}
                                    </span>
                                    <div className="flex flex-row items-center">
                                        <span className="font-inter text-sm font-medium text-tertiary mr-2">
                                        {isCompleted ? '' : isActive ? 'ENDS:' : 'STARTS:'}
                                        </span>
                                        <div className="p-2 bg-black border border-borderGray rounded-lg mr-2">
                                        <span className="font-inter font-medium text-sm text-white">
                                            {isCompleted ? '0d' : `${days}d`}
                                        </span>
                                        </div>
                                        <div className="p-2 bg-black border border-borderGray rounded-lg mr-2">
                                        <span className="font-inter font-medium text-sm text-white">
                                            {isCompleted ? '0h' : `${hours}h`}
                                        </span>
                                        </div>
                                        <div className="p-2 bg-black border border-borderGray rounded-lg">
                                        <span className="font-inter font-medium text-sm text-white">
                                            {isCompleted ? '0m' : `${minutes}m`}
                                        </span>
                                        </div>
                                    </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className="text-tertiary font-inter text-sm">No projects events available</p>
                )}
            </div>
        )}
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar - Always visible on xl+ */}
            <div className="hidden 2xl:block w-[425px]">
                {sidebarContent}
            </div>

            {/* Mobile/Tablet Sidebar - Sheet overlay */}
            <Sheet open={isOpenDaoSidebar} onOpenChange={setIsOpenDaoSidebar}>
                <SheetContent
                    side="left"
                    className={`
                        bg-black border-r border-gray-800 text-white
                        w-[90%] max-w-[425px]
                        overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                        lg:overflow-y-hidden
                    `}
                >
                    {sidebarContent}
                </SheetContent>
            </Sheet>
        </>
    );
}

export default Sidebar;